import React from "react";
import { Grid, Icon, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { FieldArray } from "formik";
import { useMaterialUIController } from "context";

// mask function
const maskCep = (value) => {
    let r = value.replace(/\D/g, "");
    if (r.length > 8) r = r.slice(0, 8);
    if (r.length > 5) r = r.replace(/^(\d{5})(\d{1,3})/, "$1-$2");
    return r;
};

export default function DevedorEnderecoSection({ values, setFieldValue, handleChange, isViewOnly, apenasValidos = true }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const visibleEnderecos = values.enderecos.filter(item => !(apenasValidos && item.cbRuim));
    const hiddenCount = values.enderecos.length - visibleEnderecos.length;

    const handleCepSearch = async (index, cepValue) => {
        const masked = maskCep(cepValue);
        setFieldValue(`enderecos[${index}].ccCep`, masked);
        
        const cleanCep = masked.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setFieldValue(`enderecos[${index}].ccLogradouro`, data.logradouro || '');
                    setFieldValue(`enderecos[${index}].ccBairro`, data.bairro || '');
                    setFieldValue(`enderecos[${index}].ccCidade`, data.localidade || '');
                    setFieldValue(`enderecos[${index}].ccEstado`, data.uf || '');
                }
            } catch (error) {
                console.error("Erro ao buscar CEP", error);
            }
        }
    };

    return (
        <MDBox mt={2}>
            <FieldArray name="enderecos">
                {({ push, remove }) => (
                    <>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDTypography variant="button" fontWeight="bold" color="text">
                                    ENDEREÇOS ({visibleEnderecos.length}{hiddenCount > 0 ? ` de ${values.enderecos.length}` : ""})
                                </MDTypography>
                                {hiddenCount > 0 && apenasValidos && (
                                    <MDTypography variant="caption" color="warning" sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                                        ({hiddenCount} inválido{hiddenCount > 1 ? "s" : ""} oculto{hiddenCount > 1 ? "s" : ""})
                                    </MDTypography>
                                )}
                            </MDBox>
                            <MDButton variant="contained" color="info" size="small" onClick={() => push({ ccCep: '', ccLogradouro: '', ccNumero: '', ccComplemento: '', ccBairro: '', ccCidade: '', ccEstado: '', cvTipo: 1, cvOrigem: 1, cbRuim: false })}>
                                <Icon sx={{ mr: 0.5 }}>add</Icon> Adicionar Endereço
                            </MDButton>
                        </MDBox>

                        {values.enderecos.length > 0 && (
                            <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {values.enderecos.map((item, index) => {
                                    const isInvalid = Boolean(item.cbRuim);
                                    if (apenasValidos && isInvalid) return null;

                                    return (
                                        <MDBox key={index} sx={{ py: 1.5, px: 1, borderBottom: "1px solid", borderColor: "divider", opacity: isInvalid ? 0.6 : 1 }}>
                                            <Grid container spacing={1.5} alignItems="flex-start">
                                                {/* Linha 1 */}
                                                <Grid item xs={12} sm={2}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="CEP"
                                                        name={`enderecos[${index}].ccCep`}
                                                        value={item.ccCep} 
                                                        onChange={(e) => handleCepSearch(index, e.target.value)} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="Logradouro"
                                                        name={`enderecos[${index}].ccLogradouro`}
                                                        value={item.ccLogradouro} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="Número"
                                                        name={`enderecos[${index}].ccNumero`}
                                                        value={item.ccNumero} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="Complemento"
                                                        name={`enderecos[${index}].ccComplemento`}
                                                        value={item.ccComplemento} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>

                                                {/* Linha 2 */}
                                                <Grid item xs={12} sm={3}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="Bairro"
                                                        name={`enderecos[${index}].ccBairro`}
                                                        value={item.ccBairro} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="Cidade"
                                                        name={`enderecos[${index}].ccCidade`}
                                                        value={item.ccCidade} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1}>
                                                    <MDInput 
                                                        fullWidth 
                                                        size="small"
                                                        label="UF"
                                                        name={`enderecos[${index}].ccEstado`}
                                                        value={item.ccEstado} 
                                                        onChange={handleChange} 
                                                        autoComplete="off"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1.5}>
                                                    <MDInput 
                                                        select 
                                                        fullWidth 
                                                        size="small"
                                                        label="Tipo"
                                                        name={`enderecos[${index}].cvTipo`}
                                                        value={item.cvTipo} 
                                                        onChange={handleChange}
                                                    >
                                                        <MenuItem value={1}>Residencial</MenuItem>
                                                        <MenuItem value={2}>Comercial</MenuItem>
                                                    </MDInput>
                                                </Grid>
                                                <Grid item xs={12} sm={1.5}>
                                                    <MDInput 
                                                        select 
                                                        fullWidth 
                                                        size="small"
                                                        label="Origem"
                                                        name={`enderecos[${index}].cvOrigem`}
                                                        value={item.cvOrigem} 
                                                        onChange={handleChange}
                                                    >
                                                        <MenuItem value={1}>Serasa</MenuItem>
                                                        <MenuItem value={2}>Boa Vista</MenuItem>
                                                        <MenuItem value={3}>Cliente</MenuItem>
                                                        <MenuItem value={4}>Informado</MenuItem>
                                                        <MenuItem value={5}>Outros</MenuItem>
                                                        <MenuItem value={6}>Remessa</MenuItem>
                                                    </MDInput>
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <MDBox display="flex" flexDirection="column" alignItems="center" width="100%">
                                                        <MDTypography
                                                            variant="caption"
                                                            color={darkMode ? "white" : "secondary"}
                                                            fontWeight="bold"
                                                            sx={({ typography: { size } }) => ({
                                                                fontSize: size.xxs,
                                                                textTransform: "uppercase",
                                                                opacity: 0.7,
                                                                whiteSpace: "nowrap",
                                                                lineHeight: 1,
                                                                mb: "4px",
                                                            })}
                                                        >
                                                            RUIM
                                                        </MDTypography>
                                                        <MDBox display="flex" alignItems="center" justifyContent="center" height="40px">
                                                            <Switch 
                                                                checked={item.cbRuim} 
                                                                onChange={(e) => setFieldValue(`enderecos[${index}].cbRuim`, e.target.checked)} 
                                                            />
                                                        </MDBox>
                                                    </MDBox>
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <MDBox display="flex" flexDirection="column" alignItems="center" width="100%">
                                                        <MDTypography
                                                            variant="caption"
                                                            color={darkMode ? "white" : "secondary"}
                                                            fontWeight="bold"
                                                            sx={({ typography: { size } }) => ({
                                                                fontSize: size.xxs,
                                                                textTransform: "uppercase",
                                                                opacity: 0.7,
                                                                whiteSpace: "nowrap",
                                                                lineHeight: 1,
                                                                mb: "4px",
                                                            })}
                                                        >
                                                            AÇÕES
                                                        </MDTypography>
                                                        <MDBox display="flex" alignItems="center" justifyContent="center" height="40px">
                                                            <IconButton color="error" size="small" onClick={() => remove(index)}>
                                                                <Icon>delete</Icon>
                                                            </IconButton>
                                                        </MDBox>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        </MDBox>
                                    );
                                })}
                                {values.enderecos.length > 0 && visibleEnderecos.length === 0 && (
                                    <MDBox py={2} textAlign="center">
                                        <MDTypography variant="caption" color="text">
                                            Todos os {values.enderecos.length} endereços cadastrados estão marcados como Ruim (ocultos pelo filtro).
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        )}
                    </>
                )}
            </FieldArray>
        </MDBox>
    );
}
